import {ENoticeTypeXsd} from "../../../../models/enums/enotice-type-xsd.enum";

/**
 * Construit les chemins des éléments duplicables en fonction du type de formulaire.
 * @param formType - Le type de formulaire (ENoticeTypeXsd)
 * @returns Les chemins des éléments duplicables pour ce type de formulaire
 */
function buildDuplicableElementPaths(formType: ENoticeTypeXsd): Record<string, string> {
  const basePath = `jsonSaisieFormulaire.ann.${formType}`;

  return {
    LOT: `${basePath}.procurementProjectLot`,
    ORG: `${basePath}.ublExtensions.ublExtension.0.extensionContent.eformsExtension.organizations.organization`,
    CON: `${basePath}.ublExtensions.ublExtension.0.extensionContent.eformsExtension.noticeResult.settledContract`,
    TPA: `${basePath}.ublExtensions.ublExtension.0.extensionContent.eformsExtension.noticeResult.tenderingParty`,
    RES: `${basePath}.ublExtensions.ublExtension.0.extensionContent.eformsExtension.noticeResult.lotResult`,
    TEN: `${basePath}.ublExtensions.ublExtension.0.extensionContent.eformsExtension.noticeResult.lotTender`,
    PAR: `${basePath}.procurementProjectLot`
  };
}

/**
 * Enum des chemins des éléments duplicables.
 * Utilise une fonction factory pour générer les chemins en fonction du type de formulaire.
 */
export class DuplicableElementPathEnum {
  private static _paths: Record<string, string>;
  private static _currentFormType: ENoticeTypeXsd;

  /**
   * Vérifie si les chemins ont été initialisés.
   * @returns true si les chemins sont initialisés, false sinon
   */
  static isInitialized(): boolean {
    return !!this._paths;
  }

  /**
   * Getter pour accéder aux chemins initialisés
   */
  static get paths(): Record<string, string> {
    if (!this._paths) {
      throw new Error('DuplicableElementPathEnum not initialized. Call initializePaths first.');
    }
    return this._paths;
  }

  /**
   * Initialise ou met à jour les chemins pour un type de formulaire donné.
   * @param formType - Le type de formulaire
   */
  static initializePaths(formType: ENoticeTypeXsd): void {
    if (this._currentFormType !== formType) {
      this._currentFormType = formType;
      this._paths = buildDuplicableElementPaths(formType);
    }
  }

  /**
   * Récupère le chemin d'un élément duplicable.
   * @param key - La clé de l'élément (LOT, ORG, etc.)
   * @returns Le chemin complet de l'élément
   * @throws Error si les chemins n'ont pas été initialisés
   */
  static getPath(key: string): string {
    if (!this._paths) {
      throw new Error('DuplicableElementPathEnum not initialized. Call initializePaths first.');
    }
    return this._paths[key];
  }

  static get LOT(): string { return this.getPath('LOT'); }
  static get ORG(): string { return this.getPath('ORG'); }
  static get CON(): string { return this.getPath('CON'); }
  static get TPA(): string { return this.getPath('TPA'); }
  static get RES(): string { return this.getPath('RES'); }
  static get TEN(): string { return this.getPath('TEN'); }
  static get PAR(): string { return this.getPath('PAR'); }
}
